import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { SessionView } from '@project/contracts'
import { Icon, LogoMark, type IconName } from '../design/index.js'
import { useApi } from './api.js'
import { Link } from './Link.js'
import { navigate, paths, type SignedInRoute } from './router.js'

interface NavItem {
  readonly key: string
  readonly label: string
  readonly icon: IconName
  readonly to: (companyId: string) => string
}

const NAV: readonly NavItem[] = [
  { key: 'home', label: 'Home', icon: 'home', to: (c) => paths.home(c) },
  { key: 'favourites', label: 'Favourites', icon: 'star', to: (c) => paths.hub(c, 'favourites') },
  { key: 'transactions', label: 'Transactions', icon: 'doc', to: (c) => paths.hub(c, 'transactions') },
  { key: 'masters', label: 'Masters', icon: 'database', to: (c) => paths.hub(c, 'masters') },
  { key: 'reports', label: 'Reports', icon: 'chart', to: (c) => paths.hub(c, 'reports') },
  { key: 'dashboards', label: 'Dashboards', icon: 'grid', to: (c) => paths.hub(c, 'dashboards') },
]

/** Words for the top bar when the page is not one of the sidebar's rows. */
const TITLES: Record<string, string> = {
  approvals: 'Approvals',
  tasks: 'Tasks',
  worklists: 'Work lists',
  alerts: 'Alerts',
  help: 'Help',
  settings: 'Settings',
  admin: 'Admin',
  password: 'Your password',
}

/** Documents are reached from the Transactions row, so they keep it lit. */
const activeNavKey = (route: SignedInRoute): string => {
  if (route.name === 'hub') return route.hub
  if (route.name === 'home') return 'home'
  if (route.name === 'settings') return 'settings'
  if (route.name === 'admin') return 'admin'
  if (route.name === 'password') return 'password'
  // A report or a dashboard keeps its own row lit, not Transactions.
  // Project Drive is listed under Reports › Files, so that row stays lit.
  if (route.name === 'report' || route.name === 'drive') return 'reports'
  if (route.name === 'dashboard') return 'dashboards'
  if (route.name === 'masterList' || route.name === 'masterRecord') return 'masters'
  return 'transactions'
}

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : ''
  return (first + last).toUpperCase()
}

const countLabel = (n: number): string => (n > 99 ? '99+' : String(n))

export interface ShellProps {
  readonly route: SignedInRoute
  readonly children: ReactNode
  readonly session: SessionView
}

export const Shell = ({ route, children, session }: ShellProps) => {
  const api = useApi()
  const companyId = route.companyId
  const active = activeNavKey(route)
  const [menuOpen, setMenuOpen] = useState(false)

  const companies = useQuery({ queryKey: ['companies', companyId], queryFn: () => api.companies(), staleTime: 5 * 60_000 })
  const home = useQuery({ queryKey: ['home', companyId], queryFn: () => api.home(), staleTime: 60_000 })

  // One call feeds all three badges; they change together, so they are fetched
  // together and refreshed on a slow interval rather than per navigation.
  const inbox = useQuery({
    queryKey: ['inboxCounts', companyId],
    queryFn: () => api.inboxCounts(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
  const counts = inbox.data ?? { approvals: 0, tasks: 0, alerts: 0 }
  const companyName = home.data?.company.name ?? ''
  const isMember = session.role === 'MEMBER'

  // On a phone the menu is a drawer: following a link closes it.
  useEffect(() => { setMenuOpen(false) }, [route])

  const title = NAV.find((n) => n.key === active)?.label ?? TITLES[active] ?? ''

  const item = (key: string, label: string, icon: IconName, to: string, count?: number, hot?: boolean) => (
    <Link
      key={key}
      to={to}
      className={key === active ? 'side-nav__item side-nav__item--active' : 'side-nav__item'}
      aria-current={key === active ? 'page' : undefined}
    >
      <Icon name={icon} size={18} />
      <span className="side-nav__label">{label}</span>
      {count && count > 0 ? (
        <span className={hot ? 'count count--hot' : 'count'}>{countLabel(count)}</span>
      ) : null}
    </Link>
  )

  return (
    <div className={menuOpen ? 'frame frame--menu-open' : 'frame'}>
      <aside className="sidebar" id="app-menu" aria-label="Main menu">
        <div className="sidebar__brand">
          <Link to={paths.home(companyId)} className="brand" aria-label="worksbq home">
            <LogoMark size={32} />
            <span className="brand__word">works<span className="brand__bq">bq</span></span>
          </Link>
          <button
            type="button"
            className="icon-btn sidebar__close"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <div className="company-switch">
          <span className="company-switch__badge" aria-hidden="true">{initials(companyName || 'Company')}</span>
          <span className="company-switch__body">
            <label className="company-switch__label" htmlFor="company-switcher">Company</label>
            <select
              id="company-switcher"
              value={companyId}
              onChange={(e) => navigate(paths.home(e.target.value))}
            >
              {(companies.data ?? []).length === 0 ? (
                <option value={companyId}>{companyName || 'This company'}</option>
              ) : (
                (companies.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))
              )}
            </select>
          </span>
          <span className="company-switch__caret" aria-hidden="true"><Icon name="updown" size={16} /></span>
        </div>

        <nav className="side-nav" aria-label="Sections">
          <span className="side-nav__heading">Menu</span>
          {NAV.map((n) => item(n.key, n.label, n.icon, n.to(companyId)))}
        </nav>

        <nav className="side-nav" aria-label="Your work">
          <span className="side-nav__heading">Your work</span>
          {item('approvals', 'Approvals', 'check', paths.hub(companyId, 'approvals'), counts.approvals)}
          {item('tasks', 'Tasks', 'clip', paths.hub(companyId, 'tasks'), counts.tasks)}
          {item('worklists', 'Work lists', 'layers', paths.hub(companyId, 'worklists'))}
          {item('alerts', 'Alerts', 'bell', paths.hub(companyId, 'alerts'), counts.alerts, true)}
        </nav>

        <span className="sidebar__spacer" />

        <nav className="side-nav side-nav--foot" aria-label="Account">
          {item('help', 'Help', 'help', paths.hub(companyId, 'help'))}
          {isMember ? null : item('settings', 'Settings', 'gear', paths.settings(companyId))}
          {isMember ? null : item('admin', 'Admin', 'shield', paths.admin(companyId, ''))}
        </nav>

        <div className="sidebar__user">
          <span className="avatar" aria-hidden="true">{initials(session.name)}</span>
          <span className="sidebar__who">
            <span className="sidebar__name">{session.name}</span>
            <span className="sidebar__email" title={session.email}>{session.email}</span>
          </span>
          <button
            type="button"
            className="icon-btn"
            aria-label="Sign out"
            title="Sign out"
            onClick={() => {
              void api.logout().finally(() => { window.location.reload() })
            }}
          >
            <Icon name="logout" size={18} />
          </button>
        </div>
      </aside>

      <div className="frame__scrim" aria-hidden="true" onClick={() => setMenuOpen(false)} />

      <div className="frame__main">
        <header className="topbar">
          <button
            type="button"
            className="icon-btn topbar__menu"
            aria-label="Open menu"
            aria-controls="app-menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Icon name="menu" size={20} />
          </button>
          <span className="topbar__title">{title}</span>
          <span className="topbar__spacer" />
          <Link
            to={paths.hub(companyId, 'approvals')}
            className="icon-btn icon-btn--round"
            aria-label={counts.approvals > 0 ? `Approvals, ${counts.approvals} waiting` : 'Approvals'}
          >
            <Icon name="inbox" size={18} />
            {counts.approvals > 0 ? <span className="icon-btn__dot" /> : null}
          </Link>
          <Link
            to={paths.hub(companyId, 'alerts')}
            className="icon-btn icon-btn--round"
            aria-label={counts.alerts > 0 ? `Alerts, ${counts.alerts} unread` : 'Alerts'}
          >
            <Icon name="bell" size={18} />
            {counts.alerts > 0 ? <span className="icon-btn__dot icon-btn__dot--hot" /> : null}
          </Link>
          <Link to={paths.password(companyId)} className="topbar__user" title={session.email}>
            <span className="avatar avatar--ink" aria-hidden="true">{initials(session.name)}</span>
            <span className="topbar__who">
              <span className="topbar__name">{session.name}</span>
              <span className="topbar__company">{companyName}</span>
            </span>
          </Link>
        </header>

        {(companies.data ?? []).find((c) => c.id === companyId)?.isTraining ? (
          <div className="training-banner" role="status" style={{ padding: '10px 24px', background: 'var(--warn-soft)', color: 'var(--warn)', borderBottom: 'var(--hair) solid var(--warn-line)' }}>
            Training company — practise freely. Nothing here is emailed out or sent to the accounting system.
          </div>
        ) : null}
        <main className="frame__sheet">{children}</main>
      </div>
    </div>
  )
}
